import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { getYear, parseISO, format, differenceInYears } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  Search, Users, UserPlus, Filter, ChevronDown, ChevronUp,
  Calendar, MoreHorizontal, Eye, Pencil, Trash2, Download, Upload, AlertCircle,
} from 'lucide-react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { usePermissions } from '../../hooks/usePermissions'
import { useTemporadaActiva } from './pagos/useTemporadaActiva'
import { useInscritosTemporada } from '../../hooks/useInscritosTemporada'
import TemporadaSelector from './pagos/TemporadaSelector'
import { FiltroInscritos, AvisoSinPadron } from './FiltroInscritos'
import { FeaturedLayout } from '../layouts'
import {
  Button, Input, Card, Badge, Label, Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue, Table, TableHeader, TableBody,
  TableHead, TableRow, TableCell, Alert, AlertDescription,
} from '../ui'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../ui/dropdown-menu'
import { YearMultiSelect } from '../ui/year-multi-select'
import { getAllPlayersWithGroups, getAllGroups, deletePlayer, getParticipacionByJugador, getFichasIncompletas } from '../../lib/supabase'
import PhotoAvatarButton from '../ui/PhotoAvatarButton'
import { isActiveGroupRelation, isVisiblePlayer, normalizePlayerStatus } from '../../lib/playerVisibility'
import { FILTROS_DEFAULT, leerFiltros, escribirFiltros } from '../../lib/playersListParams'
import ImportPlayersModal from './ImportPlayersModal'
import { useConfirmDialog } from '../../hooks/useConfirmDialog'
import { useEntitlements } from '../../contexts/EntitlementsContext'
import { UpsellModal } from '../billing/Upsell'
import { deportesDeJugador } from '../../lib/deportesJugador'
import ParticipacionBadges from './inscripciones/ParticipacionBadges'
import { useDeportes } from '../../hooks/useDeportes'

const getStatusBadgeClasses = (status) => {
  const n = status ? status.toString().trim().toLowerCase() : ''
  switch (n) {
    case 'activo': return 'bg-emerald-50 text-emerald-700 border-emerald-200'
    case 'pendiente': return 'bg-amber-50 text-amber-700 border-amber-200'
    case 'inactivo': return 'bg-slate-50 text-slate-600 border-slate-200'
    case 'suspendido': return 'bg-rose-50 text-rose-700 border-rose-200'
    case 'baja': return 'bg-rose-50 text-rose-700 border-rose-200'
    default: return 'bg-slate-50 text-slate-600 border-slate-200'
  }
}

const PlayersManager = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { isAdmin } = usePermissions()
  const { can } = useEntitlements()
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()
  const { label: deporteLabel } = useDeportes()

  // Data
  const [players, setPlayers] = useState([])
  const [groups, setGroups] = useState([])
  const [participacion, setParticipacion] = useState({})
  const [loading, setLoading] = useState(true)
  // Lo que la migracion a `personas` dejo a medias. Es contexto: si la consulta falla
  // el aviso no sale y la pantalla sigue sirviendo.
  const [incompletas, setIncompletas] = useState(0)

  // Los filtros viven en la URL, no en el estado: abrir una ficha desmonta esta
  // pantalla, y sin esto regresar la dejaba limpia. Ver src/lib/playersListParams.js.
  const [searchParams, setSearchParams] = useSearchParams()
  const filtros = useMemo(() => leerFiltros(searchParams), [searchParams])
  const {
    q: searchTerm, anios: selectedBirthYears, estatus: statusFilter,
    rama: ramaFilter, grupo: groupFilter, tipo: tipoFilter,
  } = filtros

  // `replace` para que cada tecleada no deje una entrada en el historial: si no, el
  // boton "atras" tendria que pasar por cada letra escrita en el buscador.
  const setFiltro = useCallback((cambios) => {
    setSearchParams(escribirFiltros({ ...filtros, ...cambios }), { replace: true })
  }, [filtros, setSearchParams])

  const [showFilters, setShowFilters] = useState(false)

  // El padron registrado no es el padron activo: RAIDERS tiene 206 jugadores y 39
  // inscritos. Se reusa el mismo control de las otras pantallas (por defecto solo
  // inscritos, con escape para ver al resto) en vez de inventar otro filtro: asi
  // "quien esta inscrito" tiene UNA sola definicion en todo el sistema.
  const { temporadaId, temporadas, setTemporadaId } = useTemporadaActiva()
  const inscritosEstado = useInscritosTemporada(temporadaId)

  // Modals
  const [showImport, setShowImport] = useState(false)
  const [limitUpsell, setLimitUpsell] = useState(false)

  // Load data
  const loadPlayers = useCallback(async () => {
    setLoading(true)
    const { data } = await getAllPlayersWithGroups()
    const processed = (data || [])
      .filter(isVisiblePlayer)
      .map((p) => ({
        ...p,
        grupos: (p.jugadores_grupos || [])
          .filter((rel) => rel?.grupos && isActiveGroupRelation(rel))
          .map((rel) => ({
            id: rel.grupos.id,
            nombre_del_grupo: rel.grupos.nombre_del_grupo,
            deporte: rel.grupos?.deporte || null,
          })),
        jugadores_meta: Array.isArray(p.jugadores_meta) ? p.jugadores_meta : [],
      }))
      .sort((a, b) => (a.nombre_completo || '').localeCompare(b.nombre_completo || '', 'es'))

    setPlayers(processed)
    setLoading(false)

    // Indicador de participacion (spec §4.7). Sin excluirTemporadaId: aqui no hay
    // temporada en contexto, asi que se muestran todas las abiertas.
    // Es contexto: si falla, la columna sale vacia y la tabla sigue sirviendo.
    getParticipacionByJugador()
      .then(({ data }) => setParticipacion(data || {}))
      .catch(() => setParticipacion({}))

    getFichasIncompletas()
      .then(({ data }) => setIncompletas((data || []).length))
      .catch(() => setIncompletas(0))
  }, [])

  const loadGroups = useCallback(async () => {
    const { data } = await getAllGroups()
    setGroups(data || [])
  }, [])

  // El grupo y el deporte llegan por la URL sin validar, porque cuales existen depende
  // de la org. Un id viejo (o de otra org) dejaria la lista vacia sin decir por que:
  // cuando ya se sabe que grupos hay, se cae a "todos".
  useEffect(() => {
    if (!groups.length || groupFilter === FILTROS_DEFAULT.grupo) return
    if (!groups.some((g) => String(g.id) === String(groupFilter))) {
      setFiltro({ grupo: FILTROS_DEFAULT.grupo })
    }
  }, [groups, groupFilter, setFiltro])

  useEffect(() => {
    loadPlayers()
    loadGroups()
  }, [loadPlayers, loadGroups])

  // Derived data
  const availableTipos = useMemo(() => {
    const tipos = new Set()
    players.forEach((p) => {
      deportesDeJugador({
        inscripciones: p.temporada_inscripciones,
        jugadoresGrupos: p.jugadores_grupos,
      }).forEach((d) => tipos.add(d))
    })
    return Array.from(tipos).sort((a, b) => a.localeCompare(b, 'es'))
  }, [players])

  // Filtering
  const filteredPlayers = useMemo(() => {
    // El filtro de inscripcion va sobre la LISTA, no por elemento: el hook decide
    // si filtra o no (no muerde cuando la temporada no tiene padron, ni cuando la
    // consulta falla), y esa decision no se puede tomar fila por fila.
    return inscritosEstado.filtrar(players).filter((p) => {
      const norm = normalizePlayerStatus(p?.estatus)
      const rama = (p?.genero || '').toLowerCase()

      const matchSearch =
        !searchTerm ||
        p.nombre_completo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.nickname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.numero?.toString().includes(searchTerm) ||
        p.curp?.toLowerCase().includes(searchTerm.toLowerCase())

      let matchYear = true
      if (selectedBirthYears.length > 0 && p.fecha_nacimiento) {
        try {
          matchYear = selectedBirthYears.includes(getYear(parseISO(p.fecha_nacimiento)))
        } catch { matchYear = false }
      }

      const matchStatus = statusFilter === 'todos' || norm === statusFilter
      const matchRama = ramaFilter === 'todas' || rama === ramaFilter
      const matchGroup = groupFilter === 'todos' || (p.grupos || []).some((g) => g.id === groupFilter)
      // Misma fuente que la columna "Deportes": si difieren, filtras por Flag y
      // ves filas cuyo badge dice otra cosa.
      const matchTipo = tipoFilter === 'todos' || deportesDeJugador({
        inscripciones: p.temporada_inscripciones,
        jugadoresGrupos: p.jugadores_grupos,
      }).some((d) => d.toLowerCase() === tipoFilter)

      return matchSearch && matchYear && matchStatus && matchRama && matchGroup && matchTipo
    })
  }, [players, inscritosEstado, searchTerm, selectedBirthYears, statusFilter, ramaFilter, groupFilter, tipoFilter])

  const hasActiveFilters = selectedBirthYears.length > 0 || statusFilter !== 'todos' || ramaFilter !== 'todas' || groupFilter !== 'todos' || tipoFilter !== 'todos'

  // Actions
  const handleCreate = () => {
    if (!can('players')) { setLimitUpsell(true); return; }
    navigate('/admin/jugadores/nuevo', { state: { listaQuery: location.search } })
  }

  const handleEdit = (player) => {
    navigate(`/admin/jugadores/${player.id}/editar`, { state: { listaQuery: location.search } })
  }

  const handleViewProfile = (player) => {
    navigate(`/admin/jugadores/${player.id}`, { state: { listaQuery: location.search } })
  }

  const handleDelete = async (player) => {
    if (!await confirm('Dar de baja', `Dar de baja a ${player.nombre_completo}?`, { destructive: true })) return
    const { error } = await deletePlayer(player.id)
    if (error) { await alert('Error', error.message || 'No se pudo dar de baja al jugador'); return }
    loadPlayers()
  }

  // Helpers
  const calculateAge = (dateStr) => {
    if (!dateStr) return ''
    try { return differenceInYears(new Date(), parseISO(dateStr)) } catch { return '' }
  }

  const formatBirthDate = (dateStr) => {
    if (!dateStr) return ''
    try { return format(parseISO(dateStr), 'dd/MM/yyyy', { locale: es }) } catch { return '' }
  }

  // La INSCRIPCION manda (spec §8.1). Antes salia del cache jugadores_meta, que
  // recalcula una Edge Function invocada sin revisar el error: podia quedar vieja
  // en silencio y esta columna mentia.
  const getDeportes = (p) => deportesDeJugador({
    inscripciones: p.temporada_inscripciones,
    jugadoresGrupos: p.jugadores_grupos,
  })

  if (loading) {
    return (
      <FeaturedLayout title="Gestión de Jugadores" subtitle="Administra los jugadores de tu organizacion"
      refreshAction={loadPlayers}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <span className="text-muted-foreground text-sm">Cargando jugadores...</span>
          </div>
        </div>
      </FeaturedLayout>
    )
  }

  return (
    <FeaturedLayout
      title="Gestión de Jugadores"
      subtitle="Administra los jugadores de tu organizacion"
      refreshAction={loadPlayers}
    >
      {incompletas > 0 && (
        <Alert className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
            <span>
              <strong>{incompletas}</strong> fichas están incompletas: falta quién responde,
              a qué familia pertenecen o el apellido del responsable.
            </span>
            <Button size="sm" variant="outline" onClick={() => navigate('/admin/jugadores/incompletas')}>
              Revisar
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Toolbar */}
      <div className="mb-6">
        {temporadas.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <TemporadaSelector temporadas={temporadas} value={temporadaId} onChange={setTemporadaId} />
            <FiltroInscritos estado={inscritosEstado} id="jug-ver-no-inscritos" />
          </div>
        )}
        {inscritosEstado.sinPadron && (
          <div className="mb-3"><AvisoSinPadron estado={inscritosEstado} /></div>
        )}
        <div className="flex items-center gap-2 flex-nowrap">
          <div className="flex-1 min-w-0 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              type="text"
              placeholder="Buscar por nombre, nickname, número o CURP..."
              value={searchTerm}
              onChange={(e) => setFiltro({ q: e.target.value })}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground hidden sm:block">
              <span className="font-medium text-foreground">{filteredPlayers.length}</span> jugadores
            </span>
            <Button variant="outline" size="default" onClick={() => setShowFilters(!showFilters)} className="relative h-10 w-10 p-0 sm:h-10 sm:w-auto sm:px-3">
              <Filter className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Filtros</span>
              {showFilters ? <ChevronUp className="h-4 w-4 ml-2 hidden sm:inline" /> : <ChevronDown className="h-4 w-4 ml-2 hidden sm:inline" />}
            </Button>
            <Button variant="outline" className="h-10" onClick={() => setShowImport(true)}>
              <Upload className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Importar</span>
            </Button>
            <Button onClick={handleCreate} className="h-10">
              <UserPlus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Nuevo Jugador</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mb-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">Estatus</Label>
              <Select value={statusFilter} onValueChange={(v) => setFiltro({ estatus: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="activo">Activos</SelectItem>
                  <SelectItem value="pendiente">Pendientes</SelectItem>
                  <SelectItem value="inactivo">Inactivos</SelectItem>
                  <SelectItem value="suspendido">Suspendidos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">Genero</Label>
              <Select value={ramaFilter} onValueChange={(v) => setFiltro({ rama: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todos</SelectItem>
                  <SelectItem value="varonil">Varonil</SelectItem>
                  <SelectItem value="femenil">Femenil</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">Grupo</Label>
              <Select value={groupFilter} onValueChange={(v) => setFiltro({ grupo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.nombre_del_grupo}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">Deporte</Label>
              <Select value={tipoFilter} onValueChange={(v) => setFiltro({ tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {availableTipos.map((t) => <SelectItem key={t} value={t.toLowerCase()}>{deporteLabel(t)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">Año de nacimiento</Label>
              <YearMultiSelect selectedYears={selectedBirthYears} onYearsChange={(v) => setFiltro({ anios: v })} />
            </div>
          </div>
          {hasActiveFilters && (
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setSearchParams(escribirFiltros({ ...FILTROS_DEFAULT, q: searchTerm }), { replace: true })}>
                Limpiar filtros
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      {filteredPlayers.length === 0 ? (
        <div className="text-center py-16">
          <Users className="mx-auto text-muted-foreground mb-4" size={48} />
          <p className="text-foreground font-medium mb-1">
            {searchTerm || hasActiveFilters ? 'No se encontraron jugadores' : 'No hay jugadores registrados'}
          </p>
          <p className="text-muted-foreground text-sm mb-4">
            {searchTerm || hasActiveFilters ? 'Intenta con otros filtros' : 'Comienza agregando el primer jugador'}
          </p>
          {!searchTerm && !hasActiveFilters && (
            <Button onClick={handleCreate}>
              <UserPlus className="h-4 w-4 mr-2" /> Nuevo Jugador
            </Button>
          )}
        </div>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <Table className="min-w-[900px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead className="hidden md:table-cell">F. Nacimiento</TableHead>
                  <TableHead className="hidden md:table-cell w-12">Edad</TableHead>
                  <TableHead>Estatus</TableHead>
                  <TableHead className="hidden lg:table-cell">Deportes</TableHead>
                  <TableHead className="hidden lg:table-cell">Participación</TableHead>
                  <TableHead className="hidden lg:table-cell">Grupos</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPlayers.map((p) => {
                  const deportes = getDeportes(p)
                  return (
                    <TableRow
                      key={p.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleViewProfile(p)}
                    >
                      <TableCell>
                        <PhotoAvatarButton imageUrl={p.imagen_url} name={p.nombre_completo} size="sm" onClick={(e) => { e.stopPropagation(); handleViewProfile(p) }} />
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">#{p.numero || '?'}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{p.nombre_completo}</div>
                        {p.nickname && <div className="text-xs text-muted-foreground italic">"{p.nickname}"</div>}
                        {/* El aviso de arriba da el total; aquí se ve CUÁL. Sin esto hay
                            que abrir ficha por ficha para encontrarlas. Y no es un dato
                            cosmético: sin responsable no hay a dónde mandar el
                            consentimiento, así que el jugador se queda en Pendiente --
                            53 de los 57 que están así en producción no pueden jugar. */}
                        {!p.familias?.responsable?.nombre_completo?.trim() && (
                          <div className="text-xs text-amber-700 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            Sin responsable
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm">
                        {formatBirthDate(p.fecha_nacimiento)}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm">
                        {calculateAge(p.fecha_nacimiento)}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getStatusBadgeClasses(p.estatus)} text-[10px] capitalize`}>
                          {p.estatus || 'Sin estatus'}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {deportes.slice(0, 2).map((d) => (
                            <Badge key={d} variant="outline" className="text-xs">{deporteLabel(d)}</Badge>
                          ))}
                          {deportes.length > 2 && <Badge variant="outline" className="text-xs">+{deportes.length - 2}</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <ParticipacionBadges participacion={participacion[p.id]} />
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {(p.grupos || []).slice(0, 2).map((g) => (
                            <Badge key={g.id} variant="secondary" className="text-xs">{g.nombre_del_grupo}</Badge>
                          ))}
                          {(p.grupos || []).length > 2 && <Badge variant="outline" className="text-xs">+{p.grupos.length - 2}</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                          <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => handleViewProfile(p)}>
                            <Eye className="h-3.5 w-3.5 sm:mr-1.5" />
                            <span className="hidden sm:inline text-xs">Ver</span>
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Más acciones">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEdit(p)}>
                                <Pencil className="h-4 w-4" />
                                Editar jugador
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleDelete(p)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                                Dar de baja
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Modals */}

      <ImportPlayersModal
        open={showImport}
        onOpenChange={setShowImport}
        onImported={loadPlayers}
      />

      <UpsellModal
        open={limitUpsell}
        onOpenChange={setLimitUpsell}
        feature="payments_module"
        title="Límite de jugadores alcanzado"
        message="Alcanzaste el límite de jugadores de tu plan. Mejora tu plan para agregar más."
      />
      <ConfirmDialog />
    </FeaturedLayout>
  )
}

export default PlayersManager
